import { Component, OnInit, OnDestroy, HostListener } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup, FormArray } from '@angular/forms';
import { forkJoin } from 'rxjs';
import { RmService } from '../../../services/rm/rm.service';
import { ApiServiceService } from '../../../services/common/api-service.service';

@Component({
  selector: 'app-rm-client-workspace',
  templateUrl: './rm-client-workspace.component.html',
  styleUrls: ['./rm-client-workspace.component.scss']
})
export class RmClientWorkspaceComponent implements OnInit, OnDestroy {
  clientId = '';
  loading = true;
  profile: any = null;
  notes: any[] = [];
  tasks: any[] = [];
  timeline: any[] = [];
  messages: any[] = [];
  clientPartnerPrefs: any = null;
  activeWorkspaceTab: 'profile' | 'tasks' | 'notes' | 'timeline' | 'recommendations' = 'tasks';

  stages = [
    'Searching', 'Profiles Shared', 'Interest Sent',
    'Interest Accepted', 'Parents Connected', 'Meeting Scheduled',
    'Engagement', 'Married'
  ];
  selectedStage = 'Searching';
  stageUpdateNote = '';

  newNoteText = '';
  addingNote = false;

  newTaskTitle = '';
  newTaskDueDate = '';
  newTaskPriority = 'Medium';
  addingTask = false;

  chatMessageText = '';
  sendingChat = false;

  // ─── Full-Screen Search Console ─────────────────────────────────────
  showSearchConsole = false;
  prefsBannerVisible = false;
  prefsBannerDismissed = false;

  searchKeyword = '';
  searchedProfiles: any[] = [];
  selectedProfilesForShare: any[] = [];
  searchingProfiles = false;

  // Full expanded filters
  searchFilters: any = {
    gender: '',
    min_age: '',
    max_age: '',
    height_min: '',
    height_max: '',
    marital_status: '',
    religion_id: '',
    caste_id: '',
    sub_caste_id: '',
    mother_lang_id: '',
    education_id: '',
    occupation_id: '',
    income_from: '',
    income_to: '',
    country_id: '',
    state_id: '',
    city_id: '',
    diet: '',
    smoking: '',
    drinking: '',
    rasi_id: '',
    nakshatra_id: '',
    dosham: '',
    gothram: '',
    verified_only: null,
    premium_only: false,
    recently_active: false,
    with_photo: false,
    sortBy: 'compatibility'
  };
  currentPage = 1;
  hasMoreProfiles = false;
  totalProfilesFound = 0;

  // Active filter section accordion
  openFilterSections: { [key: string]: boolean } = {
    basic: true,
    religion: true,
    education: false,
    career: false,
    location: false,
    lifestyle: false,
    horoscope: false,
    other: false
  };

  // ─── Horizontal Filter Bar (profile-list style) ──────────────────────
  isFilterCollapsed = false;
  activeTab: string = 'basic'; // 'basic', 'faith', 'education', 'career', 'location', 'lifestyle', 'horoscope', 'sort'

  activeDropdown: string | null = null;
  dropdownDirection: { [key: string]: 'up' | 'down' } = {};
  singleSearchQuery: string = '';

  rasis: any[] = [];
  natchatras: any[] = [];
  gothrams: any[] = [];
  citys: any[] = [];
  districts: any[] = [];
  states: any[] = [];
  countrys: any[] = [];
  educations: any[] = [];
  highereducations: any[] = [];
  occupations: any[] = [];
  employmentypes: any[] = [];
  religions: any[] = [];
  castes: any[] = [];
  subcastes: any[] = [];
  languages: any[] = [];
  citizens: any[] = [];
  incomes: any[] = [];

  get educationCategories(): any[] { return this.educations; }
  get educationDetailsList(): any[] { return this.highereducations; }
  get employmentTypes(): any[] { return this.employmentypes; }
  get countries(): any[] { return this.countrys; }
  get stars(): any[] { return this.natchatras; }
  get raasis(): any[] { return this.rasis; }
  doshams: any[] = [
    { _id: 'Yes', dosham_nme: 'Yes' },
    { _id: 'No', dosham_nme: 'No' },
    { _id: 'Don\'t Know', dosham_nme: 'Don\'t Know' }
  ];

  ageFromValues: number[] = [];
  ageToValues: number[] = [];
  heightFromValues: number[] = [];
  heightToValues: number[] = [];
  weightFromValues: number[] = [];
  weightToValues: number[] = [];
  salaryFromValues: number[] = [];
  salaryToValues: number[] = [];

  formGroup!: FormGroup;
  eduSearchQuery: string = '';
  higherEduSearchQuery: string = '';
  occSearchQuery: string = '';

  // ─── Dossier Side Drawer ─────────────────────────────────────────────
  showDossierDrawer = false;
  dossierCandidate: any = null;
  loadingDossier = false;
  dossierTab: 'overview' | 'family' | 'career' | 'horoscope' | 'contact' = 'overview';
  galleryActiveIndex = 0;

  // ─── Share Controls ──────────────────────────────────────────────────
  rmNoteForShare = '';
  showRmNoteInput = false;

  // ─── Recommendations Tracking ────────────────────────────────────────
  recommendationsHistory: any[] = [];
  bookmarkedCandidates: any[] = [];
  activeRecommendationTab: 'history' | 'bookmarks' = 'history';

  base64Images: { [key: string]: string } = {};
  dossierGalleryBase64: { [index: number]: string } = {};

  loadImage(imagePath: string, profileId: string): void {
    if (!imagePath || this.base64Images[profileId]) return;
    this.apiService.getImageBase64(imagePath).subscribe({
      next: (data) => {
        if (data && data.base64Image) {
          this.base64Images[profileId] = data.base64Image;
        }
      },
      error: () => {}
    });
  }

  loadDossierImages(candidate: any): void {
    this.dossierGalleryBase64 = {};
    if (!candidate) return;
    if (candidate.photo_path) {
      this.apiService.getImageBase64(candidate.photo_path).subscribe({
        next: (data) => {
          if (data && data.base64Image) {
            this.dossierGalleryBase64[0] = data.base64Image;
            const targetId = candidate._id || candidate.profile_id;
            if (targetId) this.base64Images[targetId] = data.base64Image;
          }
        }
      });
    }
    if (Array.isArray(candidate.gallery_paths)) {
      candidate.gallery_paths.forEach((p: string, idx: number) => {
        this.apiService.getImageBase64(p).subscribe({
          next: (data) => {
            if (data && data.base64Image) {
              this.dossierGalleryBase64[idx] = data.base64Image;
            }
          }
        });
      });
    }
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private rmService: RmService,
    private apiService: ApiServiceService,
    private formBuilder: FormBuilder
  ) {
    this.initForm();
    this.setupFormListeners();
  }

  private initForm() {
    this.formGroup = this.formBuilder.group({
      rasi: [''],
      natchra: [''],
      gothram: [''],
      dosham: [''],
      city: [''],
      district: [''],
      state: [''],
      country: [''],
      location: [''],
      education: this.formBuilder.array([]),
      highereducation: this.formBuilder.array([]),
      occupation: [[]],
      employmentype: [[]],
      income: [[]],
      smoking: [''],
      drinking: [''],
      food: [''],
      religion: [[]],
      caste: [[]],
      subcaste: [[]],
      agefrom: [''],
      ageto: [''],
      heightfrom: [''],
      heightto: [''],
      weightfrom: [''],
      weightto: [''],
      salaryfrom: [''],
      salaryto: [''],
      language: [[]],
      citizen: [''],
      physical: [''],
      familytype: [''],
      marital: [''],
      sort_by: ['compatibility'],
      verified_only: [false],
      with_photo_only: [false],
      recently_active_only: [false]
    });
  }

  private setupFormListeners() {
    this.formGroup.get('agefrom')?.valueChanges.subscribe(value => this.updateAgeToValues(value));
    this.formGroup.get('heightfrom')?.valueChanges.subscribe(value => this.updateHeightToValues(value));
    this.formGroup.get('weightfrom')?.valueChanges.subscribe(value => this.updateWeightToValues(value));
    this.formGroup.get('salaryfrom')?.valueChanges.subscribe(value => this.updateSalaryToValues(value));
  }

  ngOnInit(): void {
    this.clientId = this.route.snapshot.paramMap.get('id') || '';
    if (!this.clientId) {
      this.router.navigate(['/rm/dashboard']);
      return;
    }
    this.initializeRanges();
    this.loadMasterData();
    this.loadWorkspace();
  }

  private initializeRanges() {
    for (let i = 18; i <= 70; i++) this.ageFromValues.push(i);
    for (let i = 120; i <= 220; i++) this.heightFromValues.push(i);
    for (let i = 35; i <= 150; i++) this.weightFromValues.push(i);
    this.salaryFromValues = [100000, 200000, 300000, 400000, 500000, 600000, 700000, 800000, 1000000, 1200000, 1500000, 2000000, 2500000, 3000000, 3500000, 5000000, 7500000, 10000000];
  }

  updateAgeToValues(from: number) {
    this.ageToValues = this.ageFromValues.filter(age => age > from);
    const toCtrl = this.formGroup.get('ageto');
    if (toCtrl?.value && Number(toCtrl.value) <= Number(from)) {
      toCtrl.setValue('');
    }
  }

  updateHeightToValues(from: number) {
    this.heightToValues = this.heightFromValues.filter(h => h > from);
    const toCtrl = this.formGroup.get('heightto');
    if (toCtrl?.value && Number(toCtrl.value) <= Number(from)) {
      toCtrl.setValue('');
    }
  }

  updateWeightToValues(from: number) {
    this.weightToValues = this.weightFromValues.filter(w => w > from);
    const toCtrl = this.formGroup.get('weightto');
    if (toCtrl?.value && Number(toCtrl.value) <= Number(from)) {
      toCtrl.setValue('');
    }
  }

  updateSalaryToValues(from: number) {
    this.salaryToValues = this.salaryFromValues.filter(s => s > from);
    const toCtrl = this.formGroup.get('salaryto');
    if (toCtrl?.value && Number(toCtrl.value) <= Number(from)) {
      toCtrl.setValue('');
    }
  }

  loadMasterData(): void {
    forkJoin([
      this.apiService.getRasi(),
      this.apiService.getNatchra(),
      this.apiService.getGothrams(),
      this.apiService.getCity(),
      this.apiService.getDistrict(),
      this.apiService.getState(),
      this.apiService.getCountry(),
      this.apiService.getEducation(),
      this.apiService.getHigherEducation(),
      this.apiService.getOccupation(),
      this.apiService.getEmploymentType(),
      this.apiService.getReligions(),
      this.apiService.getCastes(),
      this.apiService.getSubCastes(),
      this.apiService.getLanguages(),
      this.apiService.getCitizens(),
      this.apiService.getIncome()
    ]).subscribe({
      next: ([
        rasisRes, natchatrasRes, gothramsRes, citysRes, districtsRes,
        statesRes, countrysRes, educationsRes, highereducationsRes,
        occupationsRes, employmentypesRes, religionsRes, castesRes,
        subcastesRes, languagesRes, citizensRes, incomesRes
      ]) => {
        this.rasis = rasisRes.data || [];
        this.natchatras = natchatrasRes.data || [];
        this.gothrams = gothramsRes.data || [];
        this.citys = citysRes.data || [];
        this.districts = districtsRes.data || [];
        this.states = statesRes.data || [];
        this.countrys = countrysRes.data || [];
        this.educations = educationsRes.data || [];
        this.highereducations = highereducationsRes.data || [];
        this.occupations = occupationsRes.data || [];
        this.employmentypes = employmentypesRes.data || [];
        this.incomes = incomesRes?.data || [];
        this.religions = religionsRes.data || [];
        this.castes = castesRes.data || [];
        this.subcastes = subcastesRes.data || [];
        this.languages = languagesRes.data || [];
        this.citizens = citizensRes.data || [];

        const eduArray = this.formGroup.get('education') as FormArray;
        eduArray.clear();
        this.educations.forEach(() => eduArray.push(this.formBuilder.control(false)));

        const higherEduArray = this.formGroup.get('highereducation') as FormArray;
        higherEduArray.clear();
        this.highereducations.forEach(() => higherEduArray.push(this.formBuilder.control(false)));
      },
      error: (err) => console.error('Error loading master data in RM workspace:', err)
    });
  }

  ngOnDestroy(): void {}

  loadWorkspace(): void {
    this.loading = true;
    this.rmService.getClientDetails(this.clientId).subscribe({
      next: res => {
        this.loading = false;
        if (res.status === 'success' && res.data) {
          this.profile = res.data.profile;
          this.notes = res.data.notes || [];
          this.tasks = res.data.tasks || [];
          this.timeline = res.data.timeline || [];
          this.clientPartnerPrefs = res.data.partner_preference || null;
          this.selectedStage = this.profile?.rm_stage || 'Searching';
          this.loadChat();
          this.loadRecommendationsHistory();
          this.loadBookmarks();
          // Load client's own DP via base64 (avatar_Id is a file path, not a full URL)
          const avatarPath = this.profile?.personal_info?.avatar_Id;
          const profileId = this.profile?._id;
          if (avatarPath && profileId) {
            this.loadImage(avatarPath, profileId);
          }
        } else {
          this.router.navigate(['/rm/dashboard']);
        }
      },
      error: () => {
        this.loading = false;
        this.router.navigate(['/rm/dashboard']);
      }
    });
  }

  loadChat(): void {
    this.rmService.getChatHistory(this.clientId).subscribe({
      next: res => {
        if (res.status === 'success') {
          this.messages = res.data || [];
        }
      }
    });
  }

  updateStage(): void {
    if (!this.selectedStage) return;
    this.rmService.updateStage(this.clientId, this.selectedStage, this.stageUpdateNote).subscribe({
      next: res => {
        if (res.status === 'success') {
          this.stageUpdateNote = '';
          this.loadWorkspace();
        }
      }
    });
  }

  addNote(): void {
    if (!this.newNoteText.trim()) return;
    this.addingNote = true;
    this.rmService.addNote(this.clientId, this.newNoteText).subscribe({
      next: res => {
        this.addingNote = false;
        if (res.status === 'success') {
          this.newNoteText = '';
          this.notes.unshift(res.data);
        }
      },
      error: () => { this.addingNote = false; }
    });
  }

  createTask(): void {
    if (!this.newTaskTitle.trim()) return;
    this.addingTask = true;
    this.rmService.createTask(this.clientId, {
      title: this.newTaskTitle,
      due_date: this.newTaskDueDate,
      priority: this.newTaskPriority
    }).subscribe({
      next: res => {
        this.addingTask = false;
        if (res.status === 'success') {
          this.newTaskTitle = '';
          this.newTaskDueDate = '';
          this.tasks.unshift(res.data);
        }
      },
      error: () => { this.addingTask = false; }
    });
  }

  toggleTaskStatus(task: any): void {
    const newStatus = task.status === 'Completed' ? 'Pending' : 'Completed';
    this.rmService.updateTask(this.clientId, task._id, { status: newStatus }).subscribe({
      next: res => { if (res.status === 'success') { task.status = newStatus; } }
    });
  }

  sendChatMessage(): void {
    if (!this.chatMessageText.trim()) return;
    this.sendingChat = true;
    const text = this.chatMessageText.trim();
    this.chatMessageText = '';
    this.rmService.sendMessage(this.clientId, { message_type: 'TEXT', content: text }).subscribe({
      next: res => {
        this.sendingChat = false;
        if (res.status === 'success') { this.messages.push(res.data); }
      },
      error: () => { this.sendingChat = false; }
    });
  }

  // Helper to cleanly format any preference value (object, array of objects, or primitive ID)
  getPrefDisplay(val: any, category?: string): string {
    if (val === null || val === undefined || val === '') return '';

    const resolveItem = (item: any): string => {
      if (!item) return '';
      if (typeof item === 'object') {
        return item.religion_nme || item.caste_nme || item.subcaste_nme ||
               item.city_nme || item.state_nme || item.name_1 ||
               item.country_nme || item.name_0 || item.education_nme || item.education_name ||
               item.occupation_nme || item.occupation_name || item.language_nme || item.language_name ||
               item.name || item.title || item.label || item._id || '';
      }
      if (typeof item === 'string' || typeof item === 'number') {
        const idStr = item.toString().trim();
        if (category === 'religion' || !category) {
          const f = this.religions.find(r => r._id?.toString() === idStr || r.id?.toString() === idStr);
          if (f) return f.religion_nme || f.name || idStr;
        }
        if (category === 'caste' || !category) {
          const f = this.castes.find(c => c._id?.toString() === idStr || c.id?.toString() === idStr);
          if (f) return f.caste_nme || f.name || idStr;
        }
        if (category === 'subcaste' || !category) {
          const f = this.subcastes.find(sc => sc._id?.toString() === idStr || sc.id?.toString() === idStr);
          if (f) return f.subcaste_nme || f.name || idStr;
        }
        if (category === 'city' || !category) {
          const f = this.citys.find(c => c._id?.toString() === idStr || c.id?.toString() === idStr);
          if (f) return f.city_nme || f.name || idStr;
        }
        if (category === 'state' || !category) {
          const f = this.states.find(s => s._id?.toString() === idStr || s.state_id?.toString() === idStr || s.id?.toString() === idStr);
          if (f) return f.name_1 || f.state_nme || f.name || idStr;
        }
        if (category === 'country' || !category) {
          const f = this.countrys.find(c => c._id?.toString() === idStr || c.country_id?.toString() === idStr || c.id?.toString() === idStr);
          if (f) return f.name_0 || f.country_nme || f.name || idStr;
        }
        if (category === 'education' || !category) {
          const f = this.educations.find(e => e._id?.toString() === idStr || e.id?.toString() === idStr);
          if (f) return f.education_nme || f.education_name || f.name || idStr;
        }
        if (category === 'occupation' || !category) {
          const f = this.occupations.find(o => o._id?.toString() === idStr || o.id?.toString() === idStr);
          if (f) return f.occupation_nme || f.occupation_name || f.name || idStr;
        }
        if (category === 'language' || !category) {
          const f = this.languages.find(l => l._id?.toString() === idStr || l.id?.toString() === idStr);
          if (f) return f.language_nme || f.language_name || f.name || idStr;
        }
        return idStr;
      }
      return item.toString();
    };

    if (Array.isArray(val)) {
      const names = val.map(item => resolveItem(item)).filter(n => n && n.trim() !== '');
      return names.join(', ');
    }
    return resolveItem(val);
  }

  // ─── Search Console ──────────────────────────────────────────────────
  runClientPartnerPreferencesSearch(): void {
    this.showSearchConsole = true;
    this.selectedProfilesForShare = [];
    this.rmNoteForShare = '';
    this.showRmNoteInput = false;
    this.prefsBannerDismissed = false;
    if (this.clientPartnerPrefs) {
      this.applyClientPreferences();
      this.prefsBannerVisible = true;
    } else {
      this.resetAllFilters();
    }
    this.doSearchProfiles(true);
  }

  openSearchConsole(manualSearchOnly = false): void {
    this.showSearchConsole = true;
    this.selectedProfilesForShare = [];
    this.rmNoteForShare = '';
    this.showRmNoteInput = false;
    this.prefsBannerDismissed = false;

    if (!manualSearchOnly && this.clientPartnerPrefs) {
      this.applyClientPreferences();
      this.prefsBannerVisible = true;
    } else if (manualSearchOnly) {
      this.resetAllFilters();
    }

    this.doSearchProfiles(true);
  }

  closeSearchConsole(): void {
    this.showSearchConsole = false;
    this.showDossierDrawer = false;
  }

  applyClientPreferences(): void {
    const p = this.clientPartnerPrefs;
    if (!p) return;
    const pp = p.personal_preference || {};
    const rp = p.religious_preference || {};
    const lp = p.location_preference || {};
    const ep = p.education_preference || {};
    const prp = p.profession_preference || {};
    const lfp = p.lifestyle_and_food_preference || {};

    const ageFrom = p.age_from || pp.age_from || '';
    const ageTo = p.age_to || pp.age_to || '';
    const heightFrom = p.height_from || pp.height_from || '';
    const heightTo = p.height_to || pp.height_to || '';
    const maritalStatus = p.marital_status || pp.marital_status || '';

    const relId = p.religion_id || rp.religion_id || null;
    const casteId = p.caste_id || rp.caste_id || null;
    const subcasteId = p.sub_caste_id || rp.sub_caste_id || null;

    const cityId = p.city_id || lp.city_id || '';
    const stateId = p.state_id || lp.state_id || '';
    const countryId = p.country_id || lp.country_id || '';

    if (this.formGroup) {
      this.formGroup.reset({
        rasi: '', natchra: '', gothram: '', dosham: '',
        city: cityId, district: '', state: stateId, country: countryId, location: '',
        smoking: lfp.smoking || '', drinking: lfp.drinking || '', food: lfp.food || '',
        religion: relId ? (Array.isArray(relId) ? relId : [relId._id || relId.toString()]) : [],
        caste: casteId ? (Array.isArray(casteId) ? casteId : [casteId._id || casteId.toString()]) : [],
        subcaste: subcasteId ? (Array.isArray(subcasteId) ? subcasteId : [subcasteId._id || subcasteId.toString()]) : [],
        agefrom: ageFrom, ageto: ageTo,
        heightfrom: heightFrom, heightto: heightTo,
        weightfrom: '', weightto: '', salaryfrom: '', salaryto: '',
        language: pp.language_id ? (Array.isArray(pp.language_id) ? pp.language_id : [pp.language_id._id || pp.language_id.toString()]) : [],
        citizen: '', physical: '', familytype: '', marital: maritalStatus,
        occupation: prp.occupation_id ? (Array.isArray(prp.occupation_id) ? prp.occupation_id : [prp.occupation_id._id || prp.occupation_id.toString()]) : [],
        employmentype: [], income: []
      });
      const eduVal = p.education_id || ep.education_id;
      if (eduVal) {
        const eduList = Array.isArray(eduVal) ? eduVal.map((e: any) => e._id || e.toString()) : [eduVal._id || eduVal.toString()];
        const eduArray = this.formGroup.get('education') as FormArray;
        if (eduArray && this.educations.length > 0) {
          this.educations.forEach((e, i) => {
            eduArray.at(i)?.setValue(eduList.includes(e._id));
          });
        }
      }
    }
  }

  resetToClientPreferences(): void {
    if (this.clientPartnerPrefs) {
      this.applyClientPreferences();
      this.prefsBannerVisible = true;
      this.prefsBannerDismissed = false;
    } else {
      this.resetAllFilters();
    }
    this.searchKeyword = '';
    this.doSearchProfiles(true);
  }

  resetAllFilters(): void {
    if (this.formGroup) {
      this.formGroup.reset({
        rasi: '', natchra: '', gothram: '', dosham: '',
        city: '', district: '', state: '', country: '', location: '',
        smoking: '', drinking: '', food: '',
        religion: [], caste: [], subcaste: [],
        agefrom: '', ageto: '', heightfrom: '', heightto: '',
        weightfrom: '', weightto: '', salaryfrom: '', salaryto: '',
        language: [], citizen: '', physical: '', familytype: '', marital: '',
        occupation: [], employmentype: [], income: [],
        sort_by: 'compatibility', verified_only: false, with_photo_only: false, recently_active_only: false
      });
      const eduArray = this.formGroup.get('education') as FormArray;
      if (eduArray) eduArray.controls.forEach(c => c.setValue(false));
      const higherEduArray = this.formGroup.get('highereducation') as FormArray;
      if (higherEduArray) higherEduArray.controls.forEach(c => c.setValue(false));
    }
    this.searchKeyword = '';
    this.prefsBannerVisible = false;
    this.doSearchProfiles(true);
  }

  doSearchProfiles(resetPage = true): void {
    if (resetPage) {
      this.currentPage = 1;
      this.searchedProfiles = [];
    }
    this.searchingProfiles = true;

    const filterPayload = this.getFilterPayload();
    const fv = this.formGroup?.value || {};
    const payload: any = {
      client_id: this.clientId,
      keyword: this.searchKeyword,
      sortBy: fv.sort_by || this.searchFilters.sortBy || 'compatibility',
      verified_only: fv.verified_only ? true : (this.searchFilters.verified_only || null),
      premium_only: this.searchFilters.premium_only,
      recently_active: fv.recently_active_only ? true : (this.searchFilters.recently_active || false),
      with_photo: fv.with_photo_only ? true : (this.searchFilters.with_photo || false),
      page: this.currentPage,
      limit: 18,
      data: filterPayload ? filterPayload.data : null
    };

    // Clean empty string values
    Object.keys(payload).forEach(k => {
      if (payload[k] === '' || payload[k] === undefined || payload[k] === null) delete payload[k];
    });

    this.rmService.searchProfiles(payload).subscribe({
      next: res => {
        this.searchingProfiles = false;
        if (res.status === 'success') {
          const fetched = res.data || [];
          if (resetPage) {
            this.searchedProfiles = fetched;
          } else {
            this.searchedProfiles = [...this.searchedProfiles, ...fetched];
          }
          this.searchedProfiles.forEach(p => {
            if (p.photo_path && p._id) {
              this.loadImage(p.photo_path, p._id);
            }
          });
          this.hasMoreProfiles = res.pagination?.hasMore || false;
          this.totalProfilesFound = res.pagination?.total || 0;
        }
      },
      error: () => { this.searchingProfiles = false; }
    });
  }

  applyFilter(): void {
    this.activeDropdown = null;
    this.doSearchProfiles(true);
  }

  setTab(tabId: string) {
    this.activeTab = tabId;
    this.activeDropdown = null;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    const target = event.target as HTMLElement;
    if (!target.closest('.searchable-multi-select-container')) {
      this.activeDropdown = null;
    }
  }

  toggleFilterDropdown(dropdownId: string, event: MouseEvent) {
    event.stopPropagation();
    if (this.activeDropdown === dropdownId) {
      this.activeDropdown = null;
      return;
    }
    this.activeDropdown = dropdownId;
    this.singleSearchQuery = '';
    
    const buttonRect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const spaceBelow = window.innerHeight - buttonRect.bottom;
    const spaceAbove = buttonRect.top;
    const estimatedHeight = 310;

    if (spaceBelow < estimatedHeight && spaceAbove > spaceBelow) {
      this.dropdownDirection[dropdownId] = 'up';
    } else {
      this.dropdownDirection[dropdownId] = 'down';
    }
  }

  toggleMultiSelect(controlName: string, id: string) {
    const vals = Array.isArray(this.formGroup?.value?.[controlName]) ? [...this.formGroup.value[controlName]] : [];
    const idx = vals.indexOf(id);
    if (idx >= 0) {
      vals.splice(idx, 1);
    } else {
      vals.push(id);
    }
    this.formGroup.get(controlName)?.setValue(vals);
  }

  getMultiSelectLabel(controlName: string, fallback: string, list: any[], keyName: string): string {
    const vals = this.formGroup?.value?.[controlName];
    if (!Array.isArray(vals) || vals.length === 0) return fallback;
    if (vals.length === 1) {
      const found = list.find(item => item._id === vals[0]);
      return found ? (found[keyName] || found.employment_type_nme || found.employment_name || found.income_name || found.income_nme || found.occupation_nme || found.occupation_name || found.religion_nme || found.caste_nme || found.subcaste_nme || found.language_nme || found.name) : fallback;
    }
    return `${vals.length} Selected`;
  }

  getSingleSelectLabel(controlName: string, fallback: string): string {
    const val = this.formGroup?.value?.[controlName];
    if (val === null || val === undefined || val === '') return fallback;
    
    switch (controlName) {
      case 'heightfrom':
      case 'heightto':
        return `${val} cm`;
      case 'agefrom':
      case 'ageto':
        return `${val} yrs`;
      case 'salaryfrom':
      case 'salaryto':
        return `${(Number(val) / 100000).toFixed(1)}L`;
      case 'religion': {
        const found = this.religions.find(r => r._id === val);
        return found ? (found.religion_nme || found.religion_name) : fallback;
      }
      case 'caste': {
        const found = this.castes.find(c => c._id === val);
        return found ? (found.caste_nme || found.caste_name) : fallback;
      }
      case 'subcaste': {
        const found = this.subcastes.find(s => s._id === val);
        return found ? (found.subcaste_nme || found.subcaste_name) : fallback;
      }
      case 'language': {
        const found = this.languages.find(l => l._id === val);
        return found ? (found.language_nme || found.mother_tongue || found.name) : fallback;
      }
      case 'employmentype': {
        const found = this.employmentypes.find(e => e._id === val);
        return found ? (found.employment_type_nme || found.employment_name) : fallback;
      }
      case 'country': {
        const found = this.countrys.find(c => c._id === val);
        return found ? (found.country_nme || found.name_0 || found.country_name || found.name) : fallback;
      }
      case 'citizen': {
        const found = this.citizens.find(c => c._id === val);
        return found ? (found.citizenship_nme || found.name) : fallback;
      }
      case 'state': {
        const found = this.states.find(s => s._id === val);
        return found ? (found.name_1 || found.state_nme || found.state_name || found.name) : fallback;
      }
      case 'district': {
        const found = this.districts.find(d => d._id === val);
        return found ? (found.name_2 || found.district_nme || found.district_name || found.name) : fallback;
      }
      case 'city': {
        const found = this.citys.find(c => c._id === val);
        return found ? (found.city_nme || found.city_name || found.name) : fallback;
      }
      case 'smoking':
        return val === 'No' ? 'Non-Smoker' : val === 'Yes' ? 'Smoker' : fallback;
      case 'drinking':
        return val === 'No' ? 'Non-Drinker' : val === 'Yes' ? 'Drinker' : fallback;
      case 'rasi': {
        const found = this.rasis.find(r => r._id === val);
        return found ? found.rasi_nme : fallback;
      }
      case 'natchra': {
        const found = this.natchatras.find(n => n._id === val);
        return found ? found.natchr_nme : fallback;
      }
      case 'gothram': {
        const found = this.gothrams.find(g => g._id === val);
        return found ? found.gothram_nme : fallback;
      }
      case 'dosham':
        return val === 'Yes' ? 'Yes (Dosham)' : val === 'No' ? 'No Dosham' : val;
      default:
        return val;
    }
  }

  setSingleSelect(controlName: string, value: any) {
    this.formGroup.get(controlName)?.setValue(value);
    this.activeDropdown = null;
  }

  isMultiSelected(controlName: string, id: string): boolean {
    const vals = this.formGroup?.value?.[controlName];
    return Array.isArray(vals) && vals.includes(id);
  }

  isOccSelected(id: string): boolean {
    const vals = this.formGroup?.value?.occupation;
    return Array.isArray(vals) && vals.includes(id);
  }

  toggleOccupation(id: string) {
    const vals = Array.isArray(this.formGroup?.value?.occupation) ? [...this.formGroup.value.occupation] : [];
    const idx = vals.indexOf(id);
    if (idx >= 0) {
      vals.splice(idx, 1);
    } else {
      vals.push(id);
    }
    this.formGroup.get('occupation')?.setValue(vals);
  }

  getSelectedOccCount(): number {
    const vals = this.formGroup?.value?.occupation;
    return Array.isArray(vals) ? vals.filter(Boolean).length : 0;
  }

  getSelectedOccLabel(): string {
    const count = this.getSelectedOccCount();
    if (count === 0) return 'Any Occupation';
    if (count === 1) {
      const vals = this.formGroup?.value?.occupation;
      const found = this.occupations.find(o => o._id === vals[0]);
      return found ? (found.occupation_nme || found.occupation_name) : '1 Selected';
    }
    return `${count} Occupations Selected`;
  }

  getSelectedEduCount(): number {
    const vals = this.formGroup?.value?.education;
    return Array.isArray(vals) ? vals.filter(Boolean).length : 0;
  }

  getSelectedHigherEduCount(): number {
    const vals = this.formGroup?.value?.highereducation;
    return Array.isArray(vals) ? vals.filter(Boolean).length : 0;
  }

  getSelectedEduLabel(): string {
    const count = this.getSelectedEduCount();
    if (count === 0) return 'Select Highest Qualification...';
    if (count === 1) {
      const idx = this.formGroup.value.education.findIndex((v: boolean) => v);
      return idx >= 0 && this.educations[idx] ? this.educations[idx].education_nme : `${count} Selected`;
    }
    return `${count} Qualifications Selected`;
  }

  getSelectedHigherEduLabel(): string {
    const count = this.getSelectedHigherEduCount();
    if (count === 0) return 'Select Higher / Postgraduate...';
    if (count === 1) {
      const idx = this.formGroup.value.highereducation.findIndex((v: boolean) => v);
      return idx >= 0 && this.highereducations[idx] ? this.highereducations[idx].highereducation_nme : `${count} Selected`;
    }
    return `${count} Qualifications Selected`;
  }

  get selectedQualifications(): string[] {
    return this.highereducations
      .filter((_, i) => this.formGroup.value.highereducation[i])
      .map(q => q._id);
  }

  get selectedEducations(): string[] {
    return this.educations
      .filter((_, i) => this.formGroup.value.education[i])
      .map(e => e._id);
  }

  getActiveFilterChips(): { label: string, tab: string }[] {
    const fv = this.formGroup?.value;
    if (!fv) return [];
    const chips: { label: string, tab: string }[] = [];
    const check = (v: any) => v !== '' && v !== null && v !== undefined;

    if (check(fv.agefrom) || check(fv.ageto)) chips.push({ label: `Age ${fv.agefrom || '?'}–${fv.ageto || '?'}`, tab: 'basic' });
    if (check(fv.heightfrom) || check(fv.heightto)) chips.push({ label: `Height ${fv.heightfrom || '?'}–${fv.heightto || '?'} cm`, tab: 'basic' });
    if (check(fv.weightfrom) || check(fv.weightto)) chips.push({ label: `Weight ${fv.weightfrom || '?'}–${fv.weightto || '?'} kg`, tab: 'basic' });
    if (check(fv.marital)) chips.push({ label: fv.marital, tab: 'basic' });
    if (check(fv.physical)) chips.push({ label: fv.physical, tab: 'basic' });
    if (check(fv.familytype)) chips.push({ label: fv.familytype, tab: 'basic' });
    const langs = Array.isArray(fv.language) ? fv.language.filter((v: any) => v && v !== '') : (check(fv.language) ? [fv.language] : []);
    if (langs.length > 0) {
      const l = this.languages?.find((x: any) => x._id === langs[0]);
      chips.push({ label: langs.length === 1 && l ? l.language_nme : `${langs.length} language(s)`, tab: 'basic' });
    }

    const rels = Array.isArray(fv.religion) ? fv.religion.filter((v: any) => v && v !== '') : (check(fv.religion) ? [fv.religion] : []);
    if (rels.length > 0) {
      const r = this.religions?.find((x: any) => x._id === rels[0]);
      chips.push({ label: rels.length === 1 && r ? r.religion_nme : `${rels.length} religion(s)`, tab: 'faith' });
    }
    const castes = Array.isArray(fv.caste) ? fv.caste.filter((v: any) => v && v !== '') : (check(fv.caste) ? [fv.caste] : []);
    if (castes.length > 0) {
      const c = this.castes?.find((x: any) => x._id === castes[0]);
      chips.push({ label: castes.length === 1 && c ? c.caste_nme : `${castes.length} caste(s)`, tab: 'faith' });
    }
    const subcastes = Array.isArray(fv.subcaste) ? fv.subcaste.filter((v: any) => v && v !== '') : (check(fv.subcaste) ? [fv.subcaste] : []);
    if (subcastes.length > 0) {
      const s = this.subcastes?.find((x: any) => x._id === subcastes[0]);
      chips.push({ label: subcastes.length === 1 && s ? s.subcaste_nme : `${subcastes.length} subcaste(s)`, tab: 'faith' });
    }

    if (this.selectedEducations?.length > 0) chips.push({ label: `${this.selectedEducations.length} education(s)`, tab: 'education' });
    if (this.selectedQualifications?.length > 0) chips.push({ label: `${this.selectedQualifications.length} higher edu`, tab: 'education' });

    const occs = Array.isArray(fv.occupation) ? fv.occupation.filter((v: any) => v && v !== '') : (check(fv.occupation) ? [fv.occupation] : []);
    if (occs.length > 0) {
      const o = this.occupations?.find((x: any) => x._id === occs[0]);
      chips.push({ label: occs.length === 1 && o ? (o.occupation_nme || o.occupation_name) : `${occs.length} occupation(s)`, tab: 'career' });
    }
    const emps = Array.isArray(fv.employmentype) ? fv.employmentype.filter((v: any) => v && v !== '') : (check(fv.employmentype) ? [fv.employmentype] : []);
    if (emps.length > 0) {
      const e = this.employmentypes?.find((x: any) => x._id === emps[0]);
      chips.push({ label: emps.length === 1 && e ? (e.employment_type_nme || e.employment_name) : `${emps.length} employment type(s)`, tab: 'career' });
    }
    const incs = Array.isArray(fv.income) ? fv.income.filter((v: any) => v && v !== '') : (check(fv.income) ? [fv.income] : []);
    if (incs.length > 0) {
      const inc = this.incomes?.find((x: any) => x._id === incs[0]);
      chips.push({ label: incs.length === 1 && inc ? (inc.income_name || inc.income_nme) : `${incs.length} income range(s)`, tab: 'career' });
    }
    if (check(fv.salaryfrom) || check(fv.salaryto)) chips.push({ label: `Income ${(fv.salaryfrom ? (fv.salaryfrom/100000).toFixed(1)+'L' : 'Min')}–${(fv.salaryto ? (fv.salaryto/100000).toFixed(1)+'L' : 'Max')}`, tab: 'career' });

    if (check(fv.country)) {
      const c = this.countrys?.find((x: any) => x._id === fv.country);
      if (c) chips.push({ label: c.country_nme || c.country_name || 'Country', tab: 'location' });
    }
    if (check(fv.state)) {
      const s = this.states?.find((x: any) => x._id === fv.state);
      if (s) chips.push({ label: s.state_nme || s.state_name || 'State', tab: 'location' });
    }
    if (check(fv.city)) {
      const c = this.citys?.find((x: any) => x._id === fv.city);
      if (c) chips.push({ label: c.city_nme || c.city_name || 'City', tab: 'location' });
    }
    if (check(fv.citizen)) {
      const c = this.citizens?.find((x: any) => x._id === fv.citizen);
      if (c) chips.push({ label: c.citizenship_nme || 'Citizenship', tab: 'location' });
    }

    if (check(fv.food)) chips.push({ label: fv.food, tab: 'lifestyle' });
    if (check(fv.smoking)) chips.push({ label: `Smoking: ${fv.smoking}`, tab: 'lifestyle' });
    if (check(fv.drinking)) chips.push({ label: `Drinking: ${fv.drinking}`, tab: 'lifestyle' });

    if (check(fv.rasi)) {
      const r = this.rasis?.find((x: any) => x._id === fv.rasi);
      if (r) chips.push({ label: r.rasi_nme, tab: 'horoscope' });
    }
    if (check(fv.natchra)) {
      const n = this.natchatras?.find((x: any) => x._id === fv.natchra);
      if (n) chips.push({ label: n.natchr_nme, tab: 'horoscope' });
    }
    if (check(fv.gothram)) {
      const g = this.gothrams?.find((x: any) => x._id === fv.gothram);
      if (g) chips.push({ label: g.gothram_nme, tab: 'horoscope' });
    }
    if (check(fv.dosham)) chips.push({ label: `Dosham: ${fv.dosham}`, tab: 'horoscope' });

    if (fv.verified_only) chips.push({ label: 'Verified Only', tab: 'sort' });
    if (fv.with_photo_only) chips.push({ label: 'With Photo Only', tab: 'sort' });
    if (fv.recently_active_only) chips.push({ label: 'Recently Active Only', tab: 'sort' });
    if (fv.sort_by && fv.sort_by !== 'compatibility') chips.push({ label: `Sort: ${fv.sort_by}`, tab: 'sort' });

    return chips;
  }

  onFilterChipClick(chip: { label: string, tab: string }) {
    this.isFilterCollapsed = false;
    this.setTab(chip.tab);
  }

  getActiveFilterCount(): number {
    return this.getActiveFilterChips().length;
  }

  getFilterPayload() {
    if (!this.formGroup) return null;
    const formData = this.formGroup.value;

    return {
      data: {
        horoscope_filter: {
          rasi_id: formData.rasi,
          natchr_id: formData.natchra,
          gothrm_id: formData.gothram,
          dosham: formData.dosham
        },
        location_filter: {
          city_id: formData.city,
          dist_id: formData.district,
          state_id: formData.state,
          country_id: formData.country,
          location_proximity: formData.location
        },
        education_filter: {
          education_id: this.selectedEducations,
          higher_education_id: this.selectedQualifications
        },
        profession_filter: {
          occupation_id: formData.occupation,
          employmentype_id: formData.employmentype,
          income_id: formData.income
        },
        lifestyle_and_food_filter: {
          smoking: formData.smoking,
          drinking: formData.drinking,
          food: formData.food
        },
        religious_filter: {
          religion_id: formData.religion,
          caste_id: formData.caste,
          sub_caste_id: formData.subcaste
        },
        personal_filter: {
          age_from: formData.agefrom,
          age_to: formData.ageto,
          height_from: formData.heightfrom,
          height_to: formData.heightto,
          weight_from: formData.weightfrom,
          weight_to: formData.weightto,
          salary_from: formData.salaryfrom,
          salary_to: formData.salaryto,
          language_id: formData.language,
          citizenship_id: formData.citizen,
          physical_status: formData.physical,
          family_type: formData.familytype,
          marital_status: formData.marital
        }
      }
    };
  }

  loadMoreProfiles(): void {
    if (!this.hasMoreProfiles || this.searchingProfiles) return;
    this.currentPage++;
    this.doSearchProfiles(false);
  }

  toggleFilterSection(section: string): void {
    this.openFilterSections[section] = !this.openFilterSections[section];
  }

  // ─── Dossier Drawer ──────────────────────────────────────────────────
  openDossier(candidate: any, event?: MouseEvent): void {
    if (event) event.stopPropagation();
    this.showDossierDrawer = true;
    this.loadingDossier = true;
    this.dossierCandidate = candidate;
    this.dossierTab = 'overview';
    this.galleryActiveIndex = 0;

    const targetId = candidate._id || candidate.profile_id;
    this.rmService.getCandidateProfilePreview(targetId).subscribe({
      next: res => {
        this.loadingDossier = false;
        if (res.status === 'success') {
          // Preserve selection state
          const wasSelected = this.isProfileSelected(this.dossierCandidate);
          const wasBookmarked = this.dossierCandidate.is_bookmarked;
          this.dossierCandidate = { ...res.data, is_bookmarked: wasBookmarked };
          this.loadDossierImages(this.dossierCandidate);
          if (wasSelected) {
            // Keep selection if it was already selected
            const existing = this.selectedProfilesForShare.find(x => (x._id || x.profile_id)?.toString() === targetId?.toString());
            if (!existing) this.selectedProfilesForShare.push(this.dossierCandidate);
          }
        }
      },
      error: () => { this.loadingDossier = false; }
    });
  }

  closeDossier(): void {
    this.showDossierDrawer = false;
  }

  // ─── Profile Selection & Sharing ─────────────────────────────────────
  toggleSelectProfile(p: any, event?: MouseEvent): void {
    if (event) event.stopPropagation();
    const id = (p._id || p.profile_id)?.toString();
    const idx = this.selectedProfilesForShare.findIndex(x => (x._id || x.profile_id)?.toString() === id);
    if (idx >= 0) {
      this.selectedProfilesForShare.splice(idx, 1);
    } else {
      this.selectedProfilesForShare.push(p);
    }
  }

  isProfileSelected(p: any): boolean {
    const id = (p._id || p.profile_id)?.toString();
    return this.selectedProfilesForShare.some(x => (x._id || x.profile_id)?.toString() === id);
  }

  confirmShareProfiles(): void {
    if (this.selectedProfilesForShare.length === 0) return;
    this.sendingChat = true;
    const cards = [...this.selectedProfilesForShare];
    const note = this.rmNoteForShare.trim() || undefined;

    this.rmService.shareRecommendations(this.clientId, cards, undefined, note).subscribe({
      next: res => {
        this.sendingChat = false;
        if (res.status === 'success') {
          this.messages.push(res.data);
          this.selectedProfilesForShare = [];
          this.rmNoteForShare = '';
          this.showRmNoteInput = false;
          this.loadRecommendationsHistory();
          this.closeSearchConsole();
        }
      },
      error: () => { this.sendingChat = false; }
    });
  }

  toggleCandidateBookmark(candidate: any, event?: MouseEvent): void {
    if (event) event.stopPropagation();
    const targetId = (candidate._id || candidate.profile_id)?.toString();
    this.rmService.toggleBookmark(this.clientId, targetId).subscribe({
      next: res => {
        if (res.status === 'success') {
          candidate.is_bookmarked = res.bookmarked;
          // Also update in search results and dossier
          const inResults = this.searchedProfiles.find(x => (x._id || x.profile_id)?.toString() === targetId);
          if (inResults) inResults.is_bookmarked = res.bookmarked;
          if (this.dossierCandidate && (this.dossierCandidate._id || this.dossierCandidate.profile_id)?.toString() === targetId) {
            this.dossierCandidate.is_bookmarked = res.bookmarked;
          }
          this.loadBookmarks();
        }
      }
    });
  }

  loadRecommendationsHistory(): void {
    this.rmService.getClientRecommendations(this.clientId).subscribe({
      next: res => { if (res.status === 'success') { this.recommendationsHistory = res.data || []; } }
    });
  }

  loadBookmarks(): void {
    this.rmService.getClientBookmarks(this.clientId).subscribe({
      next: res => { if (res.status === 'success') { this.bookmarkedCandidates = res.data || []; } }
    });
  }

  sendInterestOnBehalf(targetProfileId: string): void {
    if (!window.confirm('Are you sure you want to send an official Interest to this profile on behalf of your client?')) return;
    this.rmService.sendInterestForClient(this.clientId, targetProfileId).subscribe({
      next: (res: any) => {
        if (res.status === 'success') {
          window.alert('Official Interest sent successfully on behalf of client!');
          this.loadWorkspace();
        } else {
          window.alert(res.message || 'Could not send interest');
        }
      },
      error: (err: any) => { window.alert(err.error?.message || 'Error sending interest'); }
    });
  }

  openCandidatePreview(candidate: any): void {
    this.openDossier(candidate);
  }

  getClientFirstName(): string {
    return this.profile?.basic_info?.first_name || this.profile?.basic_info?.name?.split(' ')[0] || 'Client';
  }

  getScoreColor(score: number): string {
    if (score >= 90) return '#10b981'; // emerald
    if (score >= 80) return '#f59e0b'; // amber
    if (score >= 70) return '#3b82f6'; // blue
    return '#6b7280'; // gray
  }

  backToDashboard(): void {
    this.router.navigate(['/rm/dashboard']);
  }
}
